import { Tables } from '@/supabase/types'
import { ChatMessage, CodeBlock } from '@/types'

export const parseCodeBlocksAndContent = (
  content: string,
  messageId: string
): { parsedContent: string; codeBlocks: CodeBlock[] } => {
  const blocks: CodeBlock[] = []
  let remainingContent = content

  const normalizeBlockCode = (
    lang: string | undefined,
    filename: string | undefined,
    rawCode: string
  ): { language: string; filename?: string; code: string; type?: 'mermaid' } => {
    let code = rawCode.trim()
    let resolvedFilename = filename || undefined

    // Models often put `#filename=foo.html` as the first line of the code body
    // instead of (or without) the fence metadata form `#filename=foo.html#`.
    if (!resolvedFilename) {
      const inline = code.match(/^\s*#filename=([^\n#]+?)#?\s*(?:\n|$)/i)
      if (inline?.[1]) {
        resolvedFilename = inline[1].trim()
        code = code.replace(/^\s*#filename=[^\n#]+?#?\s*(?:\n|$)/i, '')
      }
    }

    const isMermaid =
      lang === 'mermaid' ||
      !!code
        .trim()
        .match(/^(graph|sequenceDiagram|classDiagram|stateDiagram|erDiagram|journey|gantt|pie)\s/)

    return {
      language: isMermaid ? 'mermaid' : lang || '',
      code: code.trim(),
      filename: resolvedFilename || undefined,
      type: isMermaid ? 'mermaid' : undefined
    }
  }

  // Updated regex to allow optional whitespace before the newline that starts the code
  const completeBlockRegex = /```([^\s`]+)?\s*(?:#filename=(.+?)#)?\s*\n([\s\S]*?)```/g
  remainingContent = remainingContent.replace(completeBlockRegex, (_, lang, filename, code) => {
    console.log('Parsing code block:', { lang, filename, codePreview: code.substring(0, 100) })

    const normalized = normalizeBlockCode(lang, filename, code)
    blocks.push({
      language: normalized.language,
      code: normalized.code,
      filename: normalized.filename,
      sequenceNo: blocks.length,
      messageId: messageId,
      type: normalized.type
    })
    return `[CODE_BLOCK_${blocks.length - 1}]`
  })

  const FILENAME_PREFIX = '#filename='

  // Updated unfinished block regex to allow optional whitespace before the newline
  const unfinishedBlockRegex = /```([^\s`]+)?\s*(#(?:(?!#).)*?#)?\s*\n?([\s\S]*)$/
  const unfinishedMatch = remainingContent.match(unfinishedBlockRegex)
  if (unfinishedMatch) {
    const [fullMatch, lang, filenameTag, code] = unfinishedMatch
    // console.log('Found unfinished block:', { lang, filenameTag, codePreview: code.substring(0, 100) });

    if (code.trim()) {
      let filename = undefined
      if (filenameTag) {
        const filenameMatch = filenameTag.match(`${FILENAME_PREFIX}(.+?)#`)
        if (filenameMatch) {
          filename = filenameMatch[1]
        }
      }

      const normalized = normalizeBlockCode(lang, filename, code)
      blocks.push({
        language: normalized.language,
        code: normalized.code,
        filename: normalized.filename,
        sequenceNo: blocks.length,
        messageId: messageId,
        type: normalized.type
      })

      remainingContent = remainingContent.replace(fullMatch, `[CODE_BLOCK_${blocks.length - 1}]`)
    }
  }

  // console.log('Parsed content:', remainingContent, 'Code blocks count:', blocks.length);

  return { parsedContent: remainingContent, codeBlocks: blocks }
}

export function parseChatMessageCodeBlocksAndContent(message: ChatMessage): ChatMessage {
  if (message.codeBlocks && message.codeBlocks.length > 0) {
    // update message ID
    message.codeBlocks.forEach((block) => {
      block.messageId = message.message.id
    })
    return message
  }

  const { parsedContent, codeBlocks } = parseCodeBlocksAndContent(
    message.message.content,
    message.message.id
  )
  return {
    ...message,
    message: {
      ...message.message,
      content: parsedContent,
      metadata: message.message.metadata ?? null,
      type: message.message.type ?? null
    },
    codeBlocks
  }
}

export const parseDBMessageCodeBlocksAndContent = (
  message: Tables<'messages'> & {
    file_items?: { id: string }[]
  }
): ChatMessage => {
  const { parsedContent, codeBlocks } = parseCodeBlocksAndContent(message.content, message.id)
  return {
    message: {
      ...message,
      content: parsedContent,
      metadata: message.metadata ?? null,
      type: message.type ?? null
    },
    fileItems: message.file_items?.map((item) => item.id) || [],
    codeBlocks
  }
}

export const reconstructContentWithCodeBlocks = (
  parsedContent: string,
  codeBlocks: CodeBlock[]
): string => {
  let reconstructedContent = parsedContent

  codeBlocks.forEach((block, index) => {
    const placeholder = `[CODE_BLOCK_${index}]`
    const codeBlockContent = `\`\`\`${block.language}${block.filename ? `\n#filename=${block.filename}#` : ''}\n${block.code}\n\`\`\``
    reconstructedContent = reconstructedContent.replace(placeholder, codeBlockContent)
  })

  return reconstructedContent
}

export const reconstructContentWithCodeBlocksInChatMessage = (
  chatMessage: ChatMessage
): ChatMessage => {
  return {
    ...chatMessage,
    message: {
      ...chatMessage.message,
      content: reconstructContentWithCodeBlocks(
        chatMessage.message.content,
        chatMessage.codeBlocks ?? []
      ),
      metadata: chatMessage.message.metadata ?? null,
      type: chatMessage.message.type ?? null
    }
  }
}
